
import { PiniaMapAction } from '@@/bits/pinia'
import { useMentionsStore } from '@@/pinia/mentions'
import mentionAutocomplete from '@@/vuedirectives/mention_autocomplete'
import { isRegistered } from 'bits/user_model'
import { Component, Getter, namespace, Prop, State, Vue } from 'bits/vue'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from 'library/v4/components/OzContainedButton.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'
import OzTextButton, { OzTextButtonColorScheme } from 'library/v4/components/OzTextButton.vue'
import RelativeTime from 'library/v4/components/relative-time.vue'
import type { Comment, Id, User } from 'types'
import AvatarImage from 'vuecomponents/avatar_image.vue'
import BaseIcon from 'vuecomponents/base_icon.vue'
import MultilineInput from 'vuecomponents/multiline_input.vue'

@Component({
  components: {
    AvatarImage,
    BaseIcon,
    MultilineInput,
    OzContainedButton,
    OzIcon,
    OzTextButton,
    RelativeTime,
  },
  directives: {
    mentionAutocomplete,
  },
})
export default class SurfacePostCommentNew extends Vue {
  OzTextButtonColorScheme = OzTextButtonColorScheme
  OzContainedButtonSizePreset = OzContainedButtonSizePreset
  OzContainedButtonColorScheme = OzContainedButtonColorScheme

  $refs!: {
    editCommentInput: MultilineInput & {
      $el: HTMLTextAreaElement
    }
  }

  @Prop() id!: Id
  @Prop() comment!: Comment & { html_body: any }
  @Prop() isBeingEdited!: boolean
  @Prop({ default: 'postView' }) usageContext!: 'postView' | 'postExpandedView'
  @Getter colorScheme!: 'dark' | 'light'
  @Getter isStream!: boolean
  @Getter dir!: string
  @State user!: User
  @Getter canIModerate!: boolean
  @namespace('userContributor').Getter getUserById
  @namespace('browser').Getter isPhone!: boolean
  @namespace('browser').State darkMode!: true | false | 'auto'
  @namespace('comment').Getter editedDraftCommentBodyFromId!: (commentId) => string

  @namespace('comment').Action updateEditedDraftCommentBody!: (payload: { commentId; body: string }) => void
  @namespace('comment').Action updateInlineCommentInputLastTypedInMs!: () => void

  @PiniaMapAction(useMentionsStore) showMentionBox!: () => void
  @PiniaMapAction(useMentionsStore) hideMentionBox!: () => void
  @PiniaMapAction(useMentionsStore) highlightPreviousSuggestion!: () => void
  @PiniaMapAction(useMentionsStore) highlightNextSuggestion!: () => void

  get isUsedInPostExpandedView(): boolean {
    return this.usageContext === 'postExpandedView'
  }

  get domCommentId(): string {
    return (this.isUsedInPostExpandedView ? 'comment-in-expanded-view-' : 'comment-') + this.id
  }

  get author(): User | null {
    return this.getUserById(this.comment.user_id)
  }

  get isRegistered(): boolean {
    if (!this.author) return false
    return isRegistered(this.author)
  }

  get hyperlinkedBody(): string {
    return this.comment.html_body || this.comment.body
  }

  get showActions(): boolean {
    return (this.user.id === this.comment.user_id || this.canIModerate) && !this.isBeingEdited
  }

  get isDark(): boolean {
    return (
      (!this.isUsedInPostExpandedView && this.colorScheme === 'dark') ||
      (this.isUsedInPostExpandedView && this.darkMode === true)
    )
  }

  mounted(): void {
    this.fetchUser(this.comment.user_id)
  }

  updated(): void {
    if (this.isBeingEdited && !this.isPhone) {
      this.$refs.editCommentInput.focus()
    }
  }

  @namespace('userContributor').Action fetchUser
  get authorName(): string {
    return this.author?.name || this.author?.username || ''
  }

  toggleDropdown(): void {
    this.$emit('show-comment-action-menu')
  }

  onEscape(): void {
    if (this.isBeingEdited) {
      this.$emit('stop-editing')
    }
  }

  onOutsideClick(): void {
    if (this.isBeingEdited) {
      this.$emit('stop-editing')
    }
  }

  submitEdit(): void {
    const newBody = this.editingDraftBody
    if (newBody?.trim() !== '') {
      this.$emit('edit', {
        id: this.id,
        body: newBody,
      })
    }
  }

  get editingDraftBody(): string {
    return this.editedDraftCommentBodyFromId(this.id)
  }

  set editingDraftBody(newBody: string) {
    this.updateEditedDraftCommentBody({ commentId: this.id, body: newBody })
  }
}
